import { useQuery } from '@tanstack/react-query'
import { Activity, ArrowUpRight, Globe2, Search, ShoppingCart, Timer, TrendingUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useApp } from '../../context/AppContext'
import { countryName } from '../../lib/activityMap'
import { getAnalyticsInterest } from '../../lib/api'
import { Card, CardContent, CardHeader, CardTitle, NadiaLoader } from '../ui'
import { ChartProvenanceCallout } from '../ChartProvenanceCallout'

const fmt = (v: number) => v.toLocaleString()

/**
 * Inteligencia de demanda near-real-time (Google Analytics):
 * interes por pais + funnel de conversion + top actividades + terminos de
 * busqueda reales + mercados en alza. Datos reales del sitio de Visit Levi.
 */
export function AnalyticsInterestPanel() {
  const { t } = useTranslation()
  const { locale } = useApp()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics-interest', locale],
    queryFn: () => getAnalyticsInterest(locale, 30),
    staleTime: 5 * 60_000,
  })

  if (isLoading) {
    return (
      <NadiaLoader
        minHeight="min-h-[280px]"
        message={t('loading.loadingAnalytics', 'Conectando con Google Analytics e intención en tiempo real...')}
        subtitle={t('loading.nadiaProcessing', 'Consultando modelos analíticos de Laponia...')}
      />
    )
  }
  if (isError || !data) return null

  const top = data.countries.slice(0, 10)
  const terms = data.search_terms.slice(0, 12)
  const rising = data.rising.slice(0, 6)

  return (
    <Card className="border">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Timer className="size-4 text-sky-500" />
            <span>{t('analytics.interestTitle', 'Demanda en tiempo real (Google Analytics)')}</span>
          </CardTitle>
          <span className="text-[10px] text-muted-foreground font-mono">
            {t('analytics.windowDays', 'Últimos')} {data.window_days} {t('analytics.days', 'días')} · {fmt(data.totals_sessions)} {t('analytics.sessions', 'sesiones')}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          {t('analytics.interestSub', 'Intención real de los visitantes del sitio de Visit Levi: quién mira ahora, qué convierte y qué busca.')}
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Mercados en alza */}
        {rising.length ? (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-400">
              <TrendingUp className="size-3.5" />
              {t('analytics.risingTitle', 'Mercados en alza (intención temprana)')}
            </div>
            <div className="flex flex-wrap gap-2">
              {rising.map((r) => (
                <span key={r.country} className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-xs">
                  {countryName(r.country, locale)}
                  <ArrowUpRight className="size-3 text-emerald-400" />
                  <b>+{r.change_pct.toFixed(0)}%</b>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {/* Interes y funnel por pais */}
        <div className="space-y-3">
          {top.map((c) => {
            const pct = Math.round(c.share * 100)
            const acts = c.activities
              .filter((a) => a.category !== 'other')
              .slice(0, 3)
              .map((a) => `${a.label} ${Math.round(a.share * 100)}%`)
            return (
              <div key={c.country} className="space-y-1">
                <div className="flex flex-wrap items-center justify-between gap-1 text-sm">
                  <span className="font-medium text-foreground flex items-center gap-1.5">
                    <Globe2 className="size-3.5 text-muted-foreground" />
                    {countryName(c.country, locale)}
                  </span>
                  <span className="font-mono font-semibold">{pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-muted/60 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-400"
                    style={{ width: `${Math.max(2, pct)}%` }}
                  />
                </div>
                {acts.length ? (
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                    <Activity className="size-3 text-teal-500" />
                    {t('analytics.topActivities', 'Top actividades')}: {acts.join(' · ')}
                  </p>
                ) : null}
              </div>
            )
          })}
        </div>

        {/* Demanda real por tipo de actividad (cruce con los POIs) */}
        {data.activities.length ? (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-teal-400">
              <Activity className="size-3.5" />
              {t('analytics.activityDemandTitle', 'Demanda por tipo de actividad (cruza con los negocios)')}
            </div>
            <div className="space-y-1.5">
              {data.activities
                .filter((a) => a.category !== 'other')
                .slice(0, 6)
                .map((a) => (
                  <div key={a.category} className="space-y-0.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground/90">{a.label}</span>
                      <span className="font-mono">{Math.round(a.share * 100)}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted/60 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-400"
                        style={{ width: `${Math.max(2, Math.round(a.share * 100))}%` }}
                      />
                    </div>
                    {a.top_countries.length ? (
                      <p className="text-[10px] text-muted-foreground">
                        {t('analytics.topCountries', 'Países top')}:{' '}
                        {a.top_countries
                          .slice(0, 3)
                          .map((c) => `${countryName(c.country, locale)} ${Math.round(c.share * 100)}%`)
                          .join(' · ')}
                      </p>
                    ) : null}
                  </div>
                ))}
            </div>
          </div>
        ) : null}

        {/* Términos de búsqueda reales */}
        {terms.length ? (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-sky-400">
              <Search className="size-3.5" />
              {t('analytics.searchTitle', 'Qué buscan en Levi')}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {terms.map((s) => (
                <span key={s.term} className="rounded-md bg-muted/70 px-2 py-1 text-[11px] font-mono text-foreground/80">
                  “{s.term}” · {s.count}
                </span>
              ))}
            </div>
          </div>
        ) : null}

          {/* Vínculo a WHAT */}
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs">
          <span className="flex items-center gap-1.5 font-semibold text-primary">
            <ShoppingCart className="size-3.5" />
            {t('analytics.whatLink', 'Usa esta demanda real para decidir qué promocionar')}
          </span>
          <span className="text-muted-foreground">
            {t('analytics.whatSub', 'Los países y categorías con más interés y conversión aquí son los que conviene destacar en la campaña.')}{' '}
            <Link to="/what" className="text-primary underline underline-offset-2">
              {t('analytics.openWhat', 'Abrir recomendaciones')}
            </Link>
          </span>
        </div>

        <ChartProvenanceCallout
          sourceName={t('chartExpl.analyticsInterest.sourceName')}
          sourceType="real"
          sourceDesc={t('chartExpl.analyticsInterest.sourceDesc')}
          insight={t('chartExpl.analyticsInterest.insight')}
        />
      </CardContent>
    </Card>
  )
}